import { Text } from "@mantine/core";
import { type ReactNode } from "react";

import {
  type RecursiveSelectItem,
  getCollectionCheckboxState,
} from "./checkboxGroup.utils";
import { StyledCheckbox } from "./CheckboxGroup.styled";
import styles from "./CheckboxGroup.module.scss";

interface Props<K extends RecursiveSelectItem> {
  item: K;
  renderLabel?: (item: K) => ReactNode;
  disabled?: boolean;
  wrapperStyles?: React.CSSProperties;
  labelStyles?: React.CSSProperties;
  selectedValues?: string[];
}

function CheckboxGroupItem<K extends RecursiveSelectItem>({
  item,
  renderLabel,
  disabled,
  wrapperStyles,
  labelStyles,
  selectedValues = [],
}: Props<K>) {
  const renderCheckbox = (
    currentItem: K,
    isNested = false,
    visited = new Set<string>(),
  ) => {
    if (visited.has(currentItem.value)) {
      return null;
    }

    const nextVisited = new Set(visited);
    nextVisited.add(currentItem.value);

    const collectionState = getCollectionCheckboxState(
      currentItem,
      selectedValues,
    );
    const isCollection = Boolean(currentItem.parts?.length);

    return (
      <div
        key={currentItem.value}
        style={{
          width: "100%",
          ...(isNested ? { paddingLeft: "24px" } : wrapperStyles),
        }}>
        <div className={isCollection ? styles.collectionHeader : undefined}>
          <StyledCheckbox
            label={
              renderLabel ? (
                renderLabel(currentItem)
              ) : (
                <Text
                  w="100%"
                  size="sm"
                  style={!isNested ? labelStyles : undefined}>
                  {currentItem.label}
                </Text>
              )
            }
            value={currentItem.value}
            {...(collectionState
              ? {
                  checked: collectionState.checked,
                  indeterminate: collectionState.indeterminate,
                }
              : {})}
            labelPosition="left"
            disabled={disabled}
          />
        </div>

        {currentItem.parts?.map(part =>
          renderCheckbox(part as K, true, nextVisited),
        )}
      </div>
    );
  };

  return renderCheckbox(item, false, new Set<string>());
}

export default CheckboxGroupItem;
